import * as React from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  FeatureFlagAvailabilitiesDocument,
  GetPostableCommunitiesDocument,
  GetPostableCommunitiesQuery,
  GetPostableCommunitiesQueryVariables,
  JoinPostsGroupDocument,
  MyCommunitiesDocument,
  OtherCommunitiesDocument,
  PostsGroupHeaderDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import 'modules/meetings/lib/utils/index.styles.css'
import CommunitiesSidebar from 'modules/posts/components/CommunitiesSidebar'
import CommunityPostList from 'modules/posts/components/CommunityPostList'
import { MembersList } from 'modules/posts/components/Groups/CommunityHeader/MembersList'
import { SetAutomaticInvitesModal } from 'modules/posts/components/Groups/DynamicGroupsAudience'
import CommunityQuickPostEditor from 'modules/posts/components/QuickPostEditor/CommunityQuickPostEditor'
import { PinnedPostsModal } from 'modules/posts/components/PinnedPostsModal'
import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'
import { buildCommunityHeaderProps } from 'modules/posts/routes/ResourceHeaders/community_header'

import './index.styles.css'

const DYNAMIC_COMMUNITIES_FEATURE_FLAG = 'dev_dynamic_communities'
const POST_PINNING_FEATURE_FLAG = 'dev_post_pinning'

type PostableCommunitiesEdge = NonNullable<
  NonNullable<GetPostableCommunitiesQuery['posts']>['groupsConnection']
>['edges'][number]

const ShowCommunityPage = () => {
  const { id } = useParams()
  const { navigation } = useNavigation()
  const { company, access, employee } = useCurrentSession()
  const navigate = useNavigate()
  const [isMembersModalOpen, setIsMembersModalOpen] = React.useState(false)
  const [isAutomaticInvitesModalOpen, setIsAutomaticInvitesModalOpen] = React.useState(false)
  const [isPinnedModalOpen, setIsPinnedModalOpen] = React.useState(false)

  const { data: featureFlagData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: company.id,
      featureNames: [DYNAMIC_COMMUNITIES_FEATURE_FLAG, POST_PINNING_FEATURE_FLAG],
    },
  })

  const isDynamicCommunitiesEnabled =
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === DYNAMIC_COMMUNITIES_FEATURE_FLAG
    )?.enabled ?? false

  const { data, refetch } = useQuery(PostsGroupHeaderDocument, {
    variables: {
      id: id ? Number(id) : 0,
    },
    skip: !id,
  })

  const isPostPinningEnabled =
    featureFlagData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === POST_PINNING_FEATURE_FLAG
    )?.enabled ?? false

  const group = data?.posts.group
  const groupTitle = group?.title
  const totalPinnedPostsCount = data?.posts.pinnedPosts.totalCount || 0

  const { nodes: postableCommunitiesNodes } = usePaginatedQuery<
    GetPostableCommunitiesQuery,
    GetPostableCommunitiesQueryVariables,
    PostableCommunitiesEdge
  >(GetPostableCommunitiesDocument, {
    extractConnection: (connectionData) =>
      connectionData.posts?.groupsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
    variables: {
      first: 100,
    },
  })

  const [joinGroup] = useMutation(JoinPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.joinPostsGroup,
  })

  const selectedCommunity: CommunitiesDropdown = React.useMemo(() => {
    if (!id) {
      return {
        title: '',
        strategy: 'all',
      }
    }
    return {
      title: groupTitle || '',
      strategy: 'group',
      value: Number(id),
    }
  }, [id, groupTitle])

  const headerProps = React.useMemo(() => {
    if (!group || !id) return null
    return buildCommunityHeaderProps({
      group,
      totalPinnedPostsCount,
      isPostPinningEnabled,
      isDynamicCommunitiesEnabled,
      setIsMembersModalOpen,
      onPinnedClick: () => setIsPinnedModalOpen(true),
    })
  }, [
    group,
    totalPinnedPostsCount,
    isPostPinningEnabled,
    isDynamicCommunitiesEnabled,
    setIsMembersModalOpen,
    id,
  ])

  const handleActionClick = async (key: string): Promise<void> => {
    if (!id) return

    switch (key) {
      case 'set-automatic-invites':
        setIsAutomaticInvitesModalOpen(true)
        break
      case 'join-group': {
        const joinResult = await joinGroup({
          variables: {
            id: Number(id),
          },
          refetchQueries: [
            {
              query: GetPostableCommunitiesDocument,
            },
            {
              query: MyCommunitiesDocument,
            },
            {
              query: OtherCommunitiesDocument,
            },
          ],
          awaitRefetchQueries: true,
        })
        const handled = handleResult(joinResult, (_errors) => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('posts.communities.error_message'),
          })
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('dashboard.groups.join.submit'),
          })
          await refetch()
        }
        break
      }
      case 'edit-community':
        navigate(navigation.communities.id.edit.fullPath({ id }))
        break
      case 'invite-people':
      case 'add-members':
        navigate(navigation.communities.id.addMembers.fullPath({ id }))
        break
      case 'remove-members':
        navigate(navigation.communities.id.removeMembers.fullPath({ id }))
        break
      case 'leave-group':
        navigate(navigation.communities.id.leave.fullPath({ id }))
        break
      case 'delete-community':
        navigate(navigation.communities.id.delete.fullPath({ id }))
        break
    }
  }

  const rightContentRef = React.useRef<HTMLDivElement>(null)
  const inCommunityPage = id !== undefined
  const postableCommunityIds = React.useMemo(
    () => new Set(postableCommunitiesNodes.map((community) => community.id)),
    [postableCommunitiesNodes]
  )
  const canPostInCurrentCommunity = id ? postableCommunityIds.has(Number(id)) : false
  const quickPostGroupId = inCommunityPage
    ? canPostInCurrentCommunity
      ? Number(id)
      : null
    : (postableCommunitiesNodes[0]?.id ?? null)
  const hasPostableCommunities = postableCommunitiesNodes.length > 0

  return (
    <Box
      width="full"
      flexDirection="row"
      height="full"
      overflowY="hidden"
      overflowX="hidden"
      style={{
        maxHeight: 'calc(100vh - 128px)',
      }}
    >
      <CommunitiesSidebar />
      <Box ref={rightContentRef} width="full" height="full" flexGrow>
        {headerProps && (
          <Box key={`community-header-${id}`} paddingX="s24" paddingY="s16" flexNoShrink>
            <ResourceHeader
              {...headerProps}
              primaryAction={
                headerProps.primaryAction
                  ? headerProps.primaryAction.items
                    ? {
                        ...headerProps.primaryAction,
                        onClick: (value: string) => handleActionClick(value),
                      }
                    : {
                        ...headerProps.primaryAction,
                        onClick: () => handleActionClick(headerProps.primaryAction?.key || ''),
                      }
                  : undefined
              }
              secondaryActions={
                headerProps.secondaryActions
                  ? headerProps.secondaryActions.map((action) => ({
                      ...action,
                      onClick: () => handleActionClick(action.key),
                    }))
                  : undefined
              }
              otherActions={
                headerProps.otherActions
                  ? headerProps.otherActions.map((action) => ({
                      ...action,
                      onClick: () => handleActionClick(action.key),
                    }))
                  : undefined
              }
            />
          </Box>
        )}
        {group && id && (
          <MembersList
            groupId={Number(id)}
            groupTitle={group.title ?? ''}
            initialAvatars={group.employees.map((groupEmployee) => ({
              firstName: groupEmployee.firstName ?? '',
              lastName: groupEmployee.lastName ?? '',
              type: 'person' as const,
              src: buildAvatarUrl(groupEmployee.avatar),
            }))}
            isOpen={isMembersModalOpen}
            onOpenChange={setIsMembersModalOpen}
            showTrigger={false}
            canManageMembers={group._policies?.update?.can && group.isMember && !group.default}
            isDynamicCommunitiesEnabled={isDynamicCommunitiesEnabled}
            dynamicTeamId={group.dynamicTeamId}
          />
        )}
        {isDynamicCommunitiesEnabled && id && group && (
          <SetAutomaticInvitesModal
            isOpen={isAutomaticInvitesModalOpen}
            onClose={() => setIsAutomaticInvitesModalOpen(false)}
            groupId={Number(id)}
            groupTitle={group.title ?? ''}
            dynamicTeamId={group.dynamicTeamId}
          />
        )}
        {id && isPostPinningEnabled && (
          <PinnedPostsModal
            isOpen={isPinnedModalOpen}
            onOpenChange={setIsPinnedModalOpen}
            selectedCommunity={selectedCommunity}
          />
        )}
        <Box
          paddingX="s24"
          paddingBottom="s24"
          data-scroll-container
          overflowY="auto"
          className={`utils_hiddenScrollBar ShowCommunityPage_communityPageBackground`}
        >
          <Box maxWidth="s600" gap="s16" width="full" alignSelf="center">
            {hasPostableCommunities && quickPostGroupId !== null && (
              <Box paddingTop="s24">
                <CommunityQuickPostEditor
                  groupId={quickPostGroupId}
                  inCommunityPage={inCommunityPage}
                  communities={postableCommunitiesNodes.map((community) => ({
                    id: community.id,
                    title: community.title,
                  }))}
                  currentUser={{
                    employeeId: employee.id,
                    firstName: access.firstName,
                    lastName: access.lastName,
                    fullName: access.fullName,
                    avatar: access.avatar,
                  }}
                />
              </Box>
            )}
            <CommunityPostList
              selectedCommunity={selectedCommunity}
              withPostBackground
            />
          </Box>
        </Box>
      </Box>
      <Outlet />
    </Box>
  )
}

export default ShowCommunityPage
